import { useEffect, type ReactNode } from 'react';
import { Link } from '../router/Link';
import {
  AccountIcon,
  Field,
  HistoryIcon,
  LockIcon,
  Notice,
  PlaylistsIcon,
  PopularIcon,
  SearchIcon,
  SettingsIcon,
  Spinner,
  SubscriptionsIcon,
  TabsImportIcon,
  ThemeIcon,
  cx,
} from '../ui';
import type { ShellBinding, ShellNavItem, ShellSearchBinding } from './model';
import { useCopy } from '../strings/useCopy';
import styles from './shell.module.css';

/** The one id the input's `aria-controls` and each option's own id are derived from. */
const SUGGESTION_LIST_ID = 'shell-search-suggestions';

/**
 * The frame every page renders inside. Renders props (R13) — the destinations, their labels, which one
 * is current and whether it is gated all arrived from `model.ts`, which got the table from core.
 *
 * One bar since the top-bar redesign: the brand is the home link (there is no separate Home entry),
 * the menu sits centred in the header, and the search is the menu's own last item — a button that
 * expands into the field where the menu stood. Below `lg` the menu leaves the header for a fixed bar
 * along the bottom of the viewport (labels always shown, never ellipsised — the bar moves down the
 * moment they would not fit) — the same `<nav>` element, repositioned by CSS, so the destinations are
 * one list wherever they are drawn.
 *
 * Landmarks, in tab order: the skip link, then `<header>` (brand, menu, search, theme, settings,
 * account), then `<main id="content">`. The skip link matters more now that the menu is inside the
 * header: without it a keyboard reader passes the brand and four destinations before the content, on
 * every page.
 */

const ICONS: Readonly<Record<ShellNavItem['id'], () => ReactNode>> = {
  popular: PopularIcon,
  subscriptions: SubscriptionsIcon,
  playlists: PlaylistsIcon,
  history: HistoryIcon,
};

function NavItem({ item }: { item: ShellNavItem }) {
  const Icon = ICONS[item.id];
  const words = useCopy().text.shell.nav;
  return (
    <Link
      href={item.href}
      className={cx(styles.item, item.current && styles.current, item.locked && styles.locked)}
      // The gate's explanation when there is one; otherwise the label, which costs a mouse user
      // nothing and names the destination in the bottom bar's cramped shape.
      title={item.title ?? item.label}
      ariaCurrent={item.current}
    >
      <span className={styles.itemIcon}>
        <Icon />
        {/* Riding the icon's top-right corner rather than sitting after the label, so the gate costs
            the item no width in the header and no third row in the bottom bar. */}
        {item.locked ? (
          <span className={styles.lock} aria-hidden="true">
            <LockIcon />
          </span>
        ) : null}
      </span>
      <span className={styles.itemLabel}>{item.label}</span>
      {/*
       * The gate, said out loud rather than left to a `title` — which a screen reader may never read
       * and a keyboard user cannot hover. The padlock is decoration; this sentence is the accessible
       * name's second half.
       */}
      {item.locked ? <span className={styles.srOnly}>{words.signInNeeded}</span> : null}
    </Link>
  );
}

/**
 * §8's `/search/suggestions`, as a `listbox` beside the input rather than a menu.
 *
 * `onMouseDown` with `preventDefault` rather than `onClick`, and that is the whole trick: a mousedown
 * on an option would otherwise blur the input first, and the delayed close in the field above exists to
 * survive that. Preventing the default keeps focus in the text field, so the row is clickable *and* the
 * caret never leaves — which is what a combobox is supposed to feel like.
 *
 * No `onFocus` on the options and no `tabIndex`: `aria-activedescendant` (on the input) is how a
 * combobox marks a current option without moving focus, and giving them focus as well would mean Tab
 * walked thirteen suggestions before leaving the header.
 */
function Suggestions({ search }: { search: ShellSearchBinding }) {
  if (!search.open) return null;

  return (
    <ul className={styles.suggestions} id={SUGGESTION_LIST_ID} role="listbox" aria-label="Suggestions">
      {search.suggestions.map((suggestion, index) => (
        <li
          key={suggestion}
          id={`${SUGGESTION_LIST_ID}-${String(index)}`}
          className={cx(styles.suggestion, index === search.activeIndex && styles.suggestionActive)}
          role="option"
          aria-selected={index === search.activeIndex}
          onMouseDown={(event) => {
            event.preventDefault();
            search.accept(index);
          }}
        >
          {suggestion}
        </li>
      ))}
    </ul>
  );
}

/**
 * The expanded search — rendered only while active, in the header's centre cell, where the menu was.
 * Focus is moved here rather than in the model: which element holds focus is a DOM concern (R13).
 */
function SearchForm({ search }: { search: ShellSearchBinding }) {
  const words = useCopy().text.shell;

  useEffect(() => {
    document.getElementById('shell-search')?.focus();
  }, []);

  return (
    <form
      className={styles.search}
      role="search"
      onSubmit={(event) => {
        event.preventDefault();
        search.submit();
      }}
    >
      <Field
        id="shell-search"
        label={words.search.label}
        hideLabel
        type="search"
        value={search.value}
        onChange={search.setValue}
        onSubmit={search.submit}
        onKeyDown={search.handleKey}
        onFocus={search.onFocus}
        /*
         * Collapsed on a *delayed* blur, not an immediate one: a mousedown on a suggestion blurs the
         * input before the click lands, so closing synchronously means every suggestion is
         * unclickable — the classic combobox bug. One tick is enough for the click to arrive.
         * Deactivating (not just closing the list) is what a blur means now that the bar expands out
         * of an icon: focus leaving the field is the reader done searching.
         */
        onBlur={() => globalThis.setTimeout(search.deactivate, 120)}
        combobox={{
          expanded: search.open,
          listId: SUGGESTION_LIST_ID,
          activeId:
            search.activeIndex < 0 ? null : `${SUGGESTION_LIST_ID}-${String(search.activeIndex)}`,
        }}
        placeholder={words.search.placeholder}
        clear={{ label: words.search.clear }}
        below={<Suggestions search={search} />}
        trailing={
          /*
           * The search button: the same act Enter performs, for the pointer. Its mousedown is
           * prevented for the ✕'s reason one level up — the press would otherwise blur the input,
           * and the delayed blur-deactivate could win the race against a slow click.
           */
          <button
            type="submit"
            className={styles.searchSubmit}
            title={words.search.submit}
            aria-label={words.search.submit}
            onMouseDown={(event) => event.preventDefault()}
          >
            <SearchIcon />
          </button>
        }
      />
    </form>
  );
}

export function AppShell({ binding, children }: { binding: ShellBinding; children: ReactNode }) {
  const { nav, brandHref, search, theme, importTabs, offline, account, settings, sessionExpiry } =
    binding;
  const words = useCopy().text.shell;

  return (
    <div className={styles.shell}>
      <a className={styles.skip} href="#content">
        {words.skipToContent}
      </a>

      <header className={cx(styles.header, search.active && styles.searchOpen)}>
        {/* The brand keeps `/` — the landing redirect — now that Home is no destination. */}
        <Link href={brandHref} className={styles.brand} title={words.brandTitle}>
          <span className={styles.brandMark} aria-hidden="true" />
          {words.brand}
        </Link>

        {/*
         * The centre cell holds both the menu and the expanded search. The menu is *always rendered*:
         * below `lg` it is the fixed bottom bar, which must stay put while a search is typed — CSS
         * hides it only where it lives in the header.
         */}
        <div className={styles.center}>
          <nav className={styles.nav} aria-label={words.nav.label}>
            {nav.map((item) => (
              <NavItem item={item} key={item.id} />
            ))}
            {/*
             * The search, as the menu's last item — a <button>, because pressing it expands the
             * field where the menu stood rather than navigating anywhere. It still carries the
             * current marker on the results page: that page is this control's destination, however
             * the reader got there.
             */}
            <button
              type="button"
              className={cx(styles.item, styles.itemButton, search.current && styles.current)}
              title={words.search.label}
              aria-current={search.current ? 'page' : undefined}
              onClick={search.activate}
            >
              <span className={styles.itemIcon}>
                <SearchIcon />
              </span>
              <span className={styles.itemLabel}>{words.search.label}</span>
            </button>
          </nav>
          {search.active ? <SearchForm search={search} /> : null}
        </div>

        <div className={styles.headerEnd}>
          {/*
            * The right-hand controls wear the menu items' clothes — the same hover wash, and
            * for the two that are destinations, the same current marker. The theme flip is a plain
            * button in the shared shape rather than an `IconButton`, which builds its own class.
            */}
          <button
            type="button"
            className={styles.iconLink}
            title={theme.resolved === 'dark' ? words.theme.toLight : words.theme.toDark}
            onClick={theme.toggle}
          >
            <ThemeIcon dark={theme.resolved === 'dark'} />
            <span className={styles.srOnly}>
              {theme.resolved === 'dark' ? words.theme.toLight : words.theme.toDark}
            </span>
          </button>
          {/*
            * Moving the other Visious tabs into this one, in the theme flip's clothes and right
            * beside it. Here rather than on the watch page because the tab somebody moves videos
            * *into* is usually the one with nothing playing in it, and a control that only existed
            * next to a video would not be there when it is wanted.
            *
            * The span is the anchor for the one thing the press can answer on its own: that no other
            * tab has videos. A `<p>` cannot live inside the button it hangs off.
            *
            * The control is also where the wait is shown, because until the other tabs answer there
            * is nothing else on screen to show it on: the icon becomes a spinner and the button stops
            * taking presses. `aria-busy` rather than a second label, so the name stays the act.
            */}
          <span className={styles.importTabs}>
            <button
              type="button"
              className={styles.iconLink}
              title={importTabs.label}
              disabled={importTabs.busy}
              aria-busy={importTabs.busy}
              onClick={importTabs.ask}
            >
              {importTabs.busy ? <Spinner small /> : <TabsImportIcon />}
              <span className={styles.srOnly}>{importTabs.label}</span>
            </button>
            {importTabs.notice === null ? null : (
              /* `live`, because it arrives in answer to the press rather than with the page. */
              <Notice live className={styles.importNotice}>
                {importTabs.notice}
              </Notice>
            )}
          </span>
          {/*
            * §8's Settings page (3e). A link with an icon and no visible label, like the theme toggle
            * beside it — `Link` rather than `IconButton`, because it is a place with a URL: the middle
            * click, the Back button and "open in a new tab" all have to work.
            */}
          <Link
            href={settings.href}
            className={cx(styles.iconLink, settings.current && styles.controlCurrent)}
            title={settings.label}
            ariaCurrent={settings.current}
          >
            <SettingsIcon />
            <span className={styles.srOnly}>{settings.label}</span>
          </Link>
          {/*
            * §8's signed-out behaviour, at the frame level: one control that is "Sign in" or the account
            * it is signed in as. Far right — the last thing in the bar, the same place every app puts
            * "who am I". It keeps its pill and its permanent outline: "who am I" is a standing
            * fact, not one destination among five.
            */}
          <Link
            href={account.href}
            className={cx(
              styles.account,
              account.signedIn && styles.accountIn,
              account.current && styles.controlCurrent,
            )}
            title={account.title}
            ariaCurrent={account.current}
          >
            <AccountIcon />
            <span className={styles.accountLabel}>{account.label}</span>
          </Link>
        </div>
      </header>

      {/*
       * `live` because this appears in response to something happening — the connection
       * going — rather than being part of the page on arrival, which is the split `Notice` documents.
       * It says what is cached and what is not, because that is the question a reader who just watched
       * an installed app open with nothing in it is actually asking.
       */}
      {offline ? (
        <Notice tone="caution" live className={styles.offline}>
          {words.offline}
        </Notice>
      ) : null}

      {/*
       * The renewal prompt (2026-08-02) — a session in its last days that cannot renew itself.
       * In the frame for the offline notice's reason: the alternative is every gated page failing at
       * once a few days from now, and one sentence beats twelve identical 403s. Not `live` — it is
       * true on arrival, not in response to anything that just happened.
       */}
      {sessionExpiry === null ? null : (
        <Notice tone="caution" className={styles.expiryNotice}>
          {sessionExpiry.text}{' '}
          <Link className={styles.expiryRenew} href={sessionExpiry.renewHref}>
            {sessionExpiry.renewLabel}
          </Link>
          <button type="button" className={styles.expiryDismiss} onClick={sessionExpiry.dismiss}>
            {sessionExpiry.dismissLabel}
          </button>
        </Notice>
      )}

      {/* `tabIndex={-1}` so the skip link can actually move focus here: without it the browser scrolls
          to the target and leaves focus in the header, and the next Tab goes back to the nav. */}
      <main className={styles.main} id="content" tabIndex={-1}>
        {children}
      </main>
    </div>
  );
}
